import React, { useCallback, useMemo, useState } from "react";
import z from "zod";

import { Button, Form, Input } from "@skydio/rivet-ui";

import { useUpdateSamlIdentityProvider } from "hooks/mutation/updateSamlIdentityProvider";
import { displayMutationResults } from "utils/mutations";

import { MetadataFileInput, MetadataUrlInput } from "./NewSamlProviderPane";
import { SsoHelpLink } from "./SsoHelpLink";

import type { samlInput } from "./NewSamlProviderPane";

interface idp {
  id: string;
  name: string;
}

interface UpdateSamlProviderPaneProps {
  idp: idp;
  onNext?: () => void;
  onCancel?: () => void;
}
const updateSamlSchema = z
  .object({
    name: z.string().optional(),
    metadataUrl: z.string().url().or(z.literal("")), // Optional URL, zod makes it annoying
    metadataFile: z.string().optional(),
  })
  .refine(
    data => {
      return (
        (data.name !== undefined && data.name.length > 0) ||
        (data.metadataUrl !== undefined && data.metadataUrl.length > 0) ||
        (data.metadataFile !== undefined && data.metadataFile.length > 0)
      );
    },
    { message: "At least one field must be set" }
  );

const UpdateSamlProviderPane = ({ idp, onNext, onCancel }: UpdateSamlProviderPaneProps) => {
  const [inputType, setInputType] = useState<samlInput>("metadataUrl");
  const [updateSamlProvider, loading] = useUpdateSamlIdentityProvider(
    displayMutationResults(response => response.updateSamlIdentityProvider, {
      onSuccess: () => {
        onNext?.();
      },
      successMessage: () => "SAML provider created successfully",
    })
  );

  const defaultValues: z.infer<typeof updateSamlSchema> = useMemo(() => {
    return {
      name: undefined,
      metadataUrl: "",
      metadataFile: undefined,
    };
  }, []);

  const handleSubmit = useCallback(
    (values: z.infer<typeof updateSamlSchema>) => {
      updateSamlProvider(
        idp.id,
        values.name ? values.name : null,
        values.metadataUrl ? values.metadataUrl : null, // Set empty string to null
        values.metadataFile ? values.metadataFile : null
      );
    },
    [idp.id, updateSamlProvider]
  );

  return (
    <Form
      schema={updateSamlSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      className="flex flex-col items-start gap-2"
    >
      <div className="w-5/6 flex-col gap-y-10">
        <Input
          name="name"
          label="Name"
          isClearable
          isDisabled={loading}
          placeholder={idp.name}
          description="Name of Identity Provider"
        />
        <br />
        {inputType === "metadataUrl" ? (
          <MetadataUrlInput setInputType={setInputType} />
        ) : (
          <MetadataFileInput setInputType={setInputType} />
        )}
      </div>
      <span className="flex w-full justify-between">
        <SsoHelpLink />
        <div className="flex gap-4 self-end">
          <Button onClick={onCancel}>Cancel</Button>
          <Button isLoading={loading} id="updateProvider" variant="primary" type="submit">
            Update
          </Button>
        </div>
      </span>
    </Form>
  );
};

export default UpdateSamlProviderPane;
